import { defineFeature } from '../define-feature';
import { TEST_IDS, sidebarNavTestId } from '../test-ids';

export default defineFeature({
  id: 'agenda.appointment.view',
  category: 'agenda',
  title: 'Consulter et naviguer dans l’agenda',
  intent:
    'Visualiser et parcourir votre agenda : voir vos rendez-vous, changer de vue et de période, filtrer par membre.',
  context: {
    why: "L'agenda est votre tableau de bord quotidien — c'est probablement l'écran que vous ouvrez le plus souvent dans votre journée. Bien le lire et y naviguer efficacement fait la différence entre gérer sereinement votre planning et perdre du temps à chercher l'information à chaque changement de contexte. Cinq vues différentes vous permettent d'adapter l'affichage à ce que vous voulez voir : la vue Jour pour un focus resserré sur aujourd'hui, la vue 3 jours pour un compromis, la vue Semaine pour la planification hebdomadaire, la vue Mois pour la charge globale et la préparation à moyen terme, et la vue Planning pour une lecture en liste linéaire particulièrement utile en fin de journée ou en revue d'équipe. Vous naviguez ensuite dans le temps avec les flèches, un bouton « Aujourd'hui » et un sélecteur de mois qui vous emmène directement où vous voulez. Si vous travaillez en équipe, un filtre par membre vous permet de zoomer sur un praticien particulier — utile pour prévoir sa charge, planifier ses congés ou lui présenter sa semaine. Enfin, le code couleur des prestations vous aide à repérer d'un coup d'œil la nature de chaque rendez-vous, sans avoir à cliquer pour lire le détail.",
    use_cases: [
      'Vous consultez votre journée du jour en début de matinée pour préparer votre journée : la vue Jour est faite pour ça.',
      "Vous planifiez votre semaine ou celle de l'équipe en début de semaine pour anticiper les charges : passez en vue Semaine.",
      "Vous préparez un tableau de charge pour la fin du mois ou une projection de chiffre d'affaires : la vue Mois donne la vision d'ensemble.",
      'Vous voulez une lecture linéaire de vos rendez-vous du jour ou de la semaine, à imprimer ou à parcourir rapidement : utilisez la vue Planning.',
      "Vous travaillez en équipe et vous voulez consulter le planning d'un praticien précis : filtrez par membre pour zoomer sur son agenda.",
      "Vous repérez d'un coup d'œil les types de rendez-vous grâce au code couleur des prestations, sans avoir à cliquer sur chaque case.",
    ],
  },
  required_scopes: ['agenda.appointment.read'],
  ui_entry_points: [{ route: '/agenda', element: 'Grille de l’agenda + barre d’outils (vues, navigation, filtres)' }],
  api: {
    primary_operation_id: 'AppointmentAppController_getAppointment',
    related_operation_ids: [
      'AppointmentAppController_getAppointmentDetail',
      'AppointmentAppController_getAppointmentVouchers',
      'ProResourceAppController_getAgendaLanes',
    ],
  },
  steps: [
    "**Ouvrez l'agenda** : accédez à l'agenda depuis la barre de navigation.",
    '**Choisissez la vue** : via le sélecteur en haut à droite, passez en Jour / 3 jours / Semaine / Mois / Planning.',
    "**Naviguez dans le temps** : utilisez « Aujourd'hui », les flèches précédent / suivant et le sélecteur de mois.",
    "**Filtrez par membre** : en équipe, affichez l'agenda d'un ou plusieurs intervenants.",
    '**Repérez et ouvrez** : repérez les RDV grâce au code couleur des prestations ; cliquez sur un RDV pour voir son détail.',
  ],
  gotchas: [
    '**5 vues disponibles** : Jour · 3 jours · Semaine · Mois · Planning (liste). Choisissez la plus lisible selon votre volume de RDV.',
    '**Vue à la volée** : la vue de départ vient des paramètres de réservation (onglet Affichage), mais vous pouvez la changer à tout moment pendant votre navigation.',
    "**Filtre par membre & droits** : un membre sans le droit « voir toute l'équipe » ne voit que son propre calendrier. Le filtre respecte les rôles.",
    "**Code couleur** : chaque RDV prend la couleur de sa prestation (ou une couleur choisie). Utile pour distinguer les types de RDV d'un coup d'œil.",
    "**Ligne d'heure courante** : un repère visuel indique l'heure actuelle sur la grille. Aide à se repérer dans la journée en cours.",
    "**Détail d'un RDV** : le clic ouvre la fiche complète (client, prestations, montant, notes, historique). C'est aussi de là que vous éditez ou annulez.",
  ],
  related_features: ['agenda.appointment.create', 'agenda.booking.settings', 'agenda.appointment.update'],
  demo_script: {
    duration_target_s: 20,
    steps: [
      {
        action: 'navigate',
        value: '/fr-FR',
        caption: 'L’agenda est votre tableau de bord quotidien : voyons comment le lire et y naviguer.',
      },
      {
        action: 'click',
        target: sidebarNavTestId('/agenda'),
        caption: 'Menu latéral, Agenda : l’écran que vous ouvrirez le plus souvent.',
      },
      {
        action: 'expect_visible',
        target: TEST_IDS.agenda_calendar,
        caption: 'Votre grille s’affiche ; vues, navigation et code couleur vous donnent tout d’un coup d’œil.',
        capture: true,
      },
    ],
  },
});

// Verbatim Kaltouma (à conserver) :
// > (Brouillon rédigé depuis les captures d'agenda — à valider.)
// > « Affichage agenda » : on consulte et on navigue dans l'agenda, on change
// > de vue (jour / 3 jours / semaine / mois / planning), le client peut
// > aussi changer la vue à la volée.
